import { memo } from 'react';
import moment, { type Moment } from 'moment-timezone';
import { Action, Dispatch, UnknownAction } from '@reduxjs/toolkit';
import {
    isEmpty,
    isString,
    isNumber,
    isUndefined,
    isArray,
    isNil,
    last,
    isObject,
    keys,
    groupBy,
    has,
    omitBy,
    values,
    isNull,
    isBoolean,
    isPlainObject,
} from 'lodash';
import { CanceledError } from 'axios';
// Import Types
import type { TFunction } from 'react-i18next';
import type { BonusSupportedCases, CurrencyAmounts, CurrencyRates, KPISupportedCase, PartnerLanguage } from 'types';
import type { GroupedOption } from 'components/common/PopoverWithOptionList/types';
// Import Store
import { useAppDispatch } from 'store';
import { resetPartnerLanguages, setPartnerLanguages } from 'store/slices/partnerLanguages';
import {
    setBaseQueryConfigurations,
    clearBaseQueryConfigurations,
    setExclusionRulesQueryConfigurations,
    clearExclusionRulesQueryConfigurations,
} from 'store/slices/queryConfigurations';
// Import Constants
import {
    ScheduleModalViews,
    WeekDaysKeyList,
    Hours,
    Days,
    DateTimeWithoutSecondsFormat,
    l,
    TimeFormatWithoutSeconds,
    DownloadFileTypes,
    Months,
    ModalActionsConfig,
    FilterComparisons,
    HexOpacityCodes,
    TemplateTypesLabels,
    DynamicBonusTypes,
    RecurrenceDataColumnsConfig,
    GlobalEvents,
    SubscriberSources,
    CommunicationTypeColorsByStatus,
    RealtimePromotionTypes,
    ReportTypesLabels,
    // BASE_DOMAIN_PLACEHOLDER,
} from 'constants/common';
import { BrowserStorageKeys } from 'constants/browserStorage';
import { POSITIVE_AMOUNT_REGEXP } from 'constants/regexp';
// Import Services
import { subscribe } from './event';
// Import Helpers
import { startHashing } from 'components/DynamicSegment/service';
// Import UI Components
import { Icon } from '@geneui/components';
// Import Components
import { UsageViewModel } from 'components/common/UsedInSection/constants';
// Import Enums
import { AwardTypesEnum, FormatNumberActionEnum, ObjectTypeIdsEnum } from 'enums';
// Import HTTP Config
import { CancelableRequest } from './http/http.config';

export const monthArrayForDropdown = (t: TFunction) => {
    return Months.map((month, i) => {
        return {
            label: t(l[month]),
            value: i,
            key: month,
        };
    });
};

export const getStringElementsByIndexArray = (array, arrayOfIndex, t: TFunction) => {
    return arrayOfIndex.reduce((acc, itemIndex, i, givenArray) => {
        acc += `${t(l[array[itemIndex]])}${i === givenArray.length - 1 ? '' : ','}`;
        return acc;
    }, '');
};

export function weeksArrayForDropdown(t) {
    return WeekDaysKeyList.map((week, i) => {
        return {
            label: t(l[week]),
            value: `${i}`,
            key: week,
        };
    });
}

export const getDays = (valueAsString = false) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const days: any[] = [];

    for (let i = 1; i <= 31; i++) {
        days.push({ label: i, value: valueAsString ? `${i}` : i });
    }

    return days;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const customMoment = (value?: Nilable<string | number | Date | Moment>) => {
    const storageTimezone = localStorage.getItem(BrowserStorageKeys.timezone);
    const timezone = !isNil(storageTimezone) && storageTimezone !== 'null' ? +storageTimezone : 0;
    const time = !isNil(value) ? value : undefined;
    return moment(time).utcOffset(timezone * 60);
};

export const customMomentWithTimezoneConversion = (
    value?: Nilable<string | number | Date | moment.Moment>,
    timezone?: number | string,
    keepLocalTime = false,
) => {
    const time = !isNil(value) ? value : undefined;
    if (isNil(timezone)) return moment(time);
    return moment(time).utcOffset(+timezone * 60, keepLocalTime);
};

export const customMomentWithoutTimezoneConversion = (
    value: Nilable<number | string | Date | moment.Moment> = undefined,
) => {
    const time = !isNil(value) ? value : undefined;
    return moment(time).utcOffset(0);
};

export const queryParamsDecoder = (search) => {
    const searchValue = search.replace('?', '');
    if (isEmpty(searchValue)) return {};
    try {
        const result = JSON.parse(decodeURIComponent(searchValue));
        return result ?? {};
    } catch (e) {
        console.error(e);
    }
    return {};
};

export const combineUrlParams = (queryParams = {}, shouldEncode = true) => {
    const params = keys(queryParams).reduce((acc, item) => {
        if (isArray(queryParams[item])) {
            acc =
                acc +
                queryParams[item].reduce((acc2, item2) => {
                    acc2 = acc2 + `${item}=${shouldEncode ? encodeURIComponent(item2) : item2}&`;
                    return acc2;
                }, '');
        } else {
            const queryItem = queryParams[item];
            acc = acc + `${item}=${shouldEncode ? encodeURIComponent(queryItem) : queryItem}&`;
        }
        return acc;
    }, '?');

    return params.slice(0, params.length - 1);
};

export const nativeQueryParamsDecoder = (params) => {
    const searchParams = new URLSearchParams(params);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: Record<string, any> = {};

    for (const [key, value] of searchParams) {
        result[key] = value;
    }

    return result;
};

export const queryParamsEncoder = (data) => {
    return `?${encodeURIComponent(JSON.stringify(data))}`;
};

export const getQueryParamNamespace = (prefix, value) => {
    return `${prefix}${value}`;
};

export const getDataTableKey = (value) => {
    return getQueryParamNamespace('dataTable', value);
};

export const getFilterHolderKey = (value) => {
    return getQueryParamNamespace('filterHolder', value);
};

export const getFiltersTagKey = (value) => {
    return getQueryParamNamespace('filtersTagGroup', value);
};

export const openUrlInNewTab = (url) => {
    if (!isEmpty(url)) return;
    const tab = window.open(url, '_blank');
    tab?.focus();
};

export const getByteLen = (normalVal) => {
    normalVal = String(normalVal);

    let byteLen = 0;
    for (let i = 0; i < normalVal.length; i++) {
        const c = normalVal.charCodeAt(i);
        byteLen +=
            c < 1 << 7
                ? 1
                : c < 1 << 11
                  ? 2
                  : c < 1 << 16
                    ? 3
                    : c < 1 << 21
                      ? 4
                      : c < 1 << 26
                        ? 5
                        : c < 1 << 31
                          ? 6
                          : Number.NaN;
    }
    return byteLen;
};

export const Years = () => {
    const years: string[] = [];
    const dateStart = customMoment();
    const dateEnd = customMoment().add(11, 'y');
    while (dateEnd.diff(dateStart, 'years') >= 0) {
        years.push(dateStart.format('YYYY'));
        dateStart.add(1, 'year');
    }

    return years;
};

export const cronStringEncoder = (key = '', value: Record<string, (string | string)[]> = {}) => {
    const { hours, days, weeks, months, years, hour, day, month, year } = value;
    const hoursValues: string[] = [];
    const daysValues: string[] = [];
    const weeksValues: string[] = [];
    const monthsValues: string[] = [];
    const yearsValues: string[] = [];

    for (let i = 0; i < hours?.length; i++) {
        hoursValues.push(Hours[hours[i]]);
    }
    for (let i = 0; i < days?.length; i++) {
        daysValues.push(Days[days[i]]);
    }
    for (let i = 0; i < weeks?.length; i++) {
        weeksValues.push(WeekDaysKeyList[weeks[i]]);
    }
    for (let i = 0; i < months?.length; i++) {
        monthsValues.push(Days[months[i]]);
    }
    for (let i = 0; i < years?.length; i++) {
        yearsValues.push(Years()[years[i]]);
    }

    switch (key) {
        case ScheduleModalViews.hourlyView:
            if (isEmpty(hours)) {
                return `${hour ? `*/${hour}` : '*/'} * * ? *`;
            } else {
                return `${hoursValues} * * ? *`;
            }
        case ScheduleModalViews.dailyView:
            if (isEmpty(days)) {
                return `${day ? `*/${day}` : '*/'} * ? *`;
            } else {
                return `${daysValues} * ? *`;
            }
        case ScheduleModalViews.weeklyView:
            return `? * ${isEmpty(weeksValues) ? '*/' : weeksValues} *`;
        case ScheduleModalViews.monthlyView:
            return (
                (isEmpty(days) ? `${day ? `*/${day}` : '*/'}` : daysValues) +
                ' ' +
                (isEmpty(months) ? `${month ? `*/${month}` : '*/'}` : monthsValues) +
                ' ? *'
            );
        case ScheduleModalViews.annuallyView:
            return (
                (isEmpty(days) ? `${day ? `*/${day}` : '*/'}` : daysValues) +
                ' ' +
                (isEmpty(months) ? `${month ? `*/${month}` : '*/'}` : monthsValues) +
                ' ? ' +
                (isEmpty(years) ? `${year ? `*/${year}` : '*/'}` : yearsValues)
            );
        default:
            return '';
    }
};

export const cronStringDecoder = (value: Nullable<string> = '') => {
    const result: AnyObject = {
        startTime: customMoment().format(TimeFormatWithoutSeconds),
        startDate: customMoment().format(),
        endDate: customMoment().format(),
        hour: '',
        day: '',
        month: '',
        year: '',
        hours: [],
        days: [],
        weeks: [],
        months: [],
        years: [],
        viewType: ScheduleModalViews.mainView,
    };

    result.viewType = ScheduleModalViews.notRepeatView;

    if (!isEmpty(value)) {
        const splittedCron = value?.split('?');
        const leftChunk = splittedCron?.[0].split(' ');
        const rightChunk = splittedCron?.[1].split(' ');
        const rightChunkAsteriskCount =
            splittedCron?.[1].match(/\*/g) === null ? 0 : splittedCron?.[1]?.match(/\*/g)?.length;
        const hourChunk = leftChunk?.[2] || '';
        const dayChunk = leftChunk?.[3] || '';
        const weekChunk = rightChunkAsteriskCount ? rightChunk?.[2] || '' : '';
        const monthChunk = leftChunk?.[4] || '';
        const yearChunk = rightChunk?.[1] || '';

        const isSpecificHoursSelected =
            dayChunk === '*' &&
            weekChunk === '' &&
            monthChunk === '*' &&
            yearChunk === '*' &&
            hourChunk.length > 0 &&
            !hourChunk.includes('*');
        const isHourIntervalSelected = hourChunk.includes('/');

        if (isSpecificHoursSelected || isHourIntervalSelected) {
            result.hours = isSpecificHoursSelected
                ? hourChunk
                      .split(',')
                      .filter((hour) => hour)
                      .map((hour) => {
                          return Hours.indexOf(hour);
                      })
                : [];
            result.hour = isHourIntervalSelected ? hourChunk.split('/')[1] : '';
            result.startTime = `00:${leftChunk?.[1]}`;
        } else {
            result.startTime = `${hourChunk}:${leftChunk?.[1]}`;
        }

        if (dayChunk.includes('*')) {
            result.day = dayChunk.substr(2);
        } else {
            result.days = dayChunk
                .split(',')
                .filter((day) => day)
                .map((day) => {
                    return Days.indexOf(day);
                });
        }

        if (rightChunkAsteriskCount === 2) {
            leftChunk?.push('');
            result.weeks = weekChunk
                .split(',')
                .filter((weekDayKey) => weekDayKey)
                .map((weekDayKey) => {
                    return WeekDaysKeyList.indexOf(weekDayKey);
                });
        }

        if (monthChunk.includes('*')) {
            result.month = monthChunk.substr(2);
        } else {
            result.months = monthChunk
                .split(',')
                .filter((month) => month)
                .map((month) => {
                    return Days.indexOf(month);
                });
        }

        if (yearChunk.includes('*')) {
            result.year = yearChunk.substr(2);
        } else {
            result.years = yearChunk
                .split(',')
                .filter((year) => year)
                .map((year) => {
                    return Years().indexOf(year);
                });
        }

        const isHourSelected = result.hour || result.hours.length || hourChunk[1] === '/';
        const isDaySelected = result.day || result.days.length || dayChunk[1] === '/';
        const isWeekSelected = result.weeks.length || weekChunk[1] === '/';
        const isMonthSelected = result.month || result.months.length || monthChunk[1] === '/';
        const isYearSelected = result.year || result.years.length || yearChunk[1] === '/';

        if (isWeekSelected) {
            result.viewType = ScheduleModalViews.weeklyView;
        } else if (isYearSelected) {
            result.viewType = ScheduleModalViews.annuallyView;
        } else if (isMonthSelected) {
            result.viewType = ScheduleModalViews.monthlyView;
        } else if (isDaySelected) {
            result.viewType = ScheduleModalViews.dailyView;
        } else if (isHourSelected) {
            result.viewType = ScheduleModalViews.hourlyView;
        }
    }

    return result;
};

export const getDateFormat = (date, timeZone = 0, format = DateTimeWithoutSecondsFormat) => {
    return moment.utc(date).utcOffset(timeZone).format(format);
};

export const getDate = (date, timeZone = 0) => {
    return moment(date).utcOffset(timeZone * 60);
};

export const getDateWithTime = (value, time, momentFunc = moment) => {
    return momentFunc(value).set('hour', time[0]).set('minute', time[1]);
};

export const convertMinutesToHHMM = (minutes) => {
    const duration = moment.duration(minutes, 'minutes');
    const hours = Math.floor(duration.asHours());
    const mins = duration.minutes();

    return moment({ hours, minutes: mins }).format('HH:mm');
};

export const generateRequestFilterField = (
    name?: string,
    comparison?: number,
    values?: boolean | string | string[] | number | number[],
) => {
    if (!isString(name) && !isNumber(comparison)) return;

    let correctValue;
    if (comparison !== FilterComparisons.IsBlank) {
        if (values === 'true' || values === true) {
            correctValue = [1];
        } else if (values === 'false' || values === false) {
            correctValue = [0];
        } else if (isUndefined(values)) {
            correctValue = [];
        } else if (isArray(values)) {
            correctValue = values;
            // TODO: see case when values = ['true', 'false']
        } else {
            correctValue = [values];
        }
    } else {
        if (isUndefined(values)) {
            correctValue = [];
        } else {
            correctValue = [values];
        }
    }

    return {
        Name: name,
        Comparision: comparison,
        Values: correctValue,
    };
};

export const hexToRgb = (hexCode) => {
    const colorIngredients = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexCode);
    let result: { r: number; g: number; b: number } | null = null;

    if (!isEmpty(colorIngredients)) {
        result = {
            r: parseInt(colorIngredients?.[1] ?? '', 16),
            g: parseInt(colorIngredients?.[2] ?? '', 16),
            b: parseInt(colorIngredients?.[3] ?? '', 16),
        };
    }

    return !isNil(result) ? `${result.r} ${result.g} ${result.b}` : false;
};

export const getNamesString = (list = []) => {
    if (!isArray(list)) return;
    return list.map(({ Name }) => Name).toString();
};

export const getUsedInConfirmationText = (t, typeLabel, list = [], isUsedInObjectsLabel) => {
    const types = groupBy(list, 'Type');
    const tObject = { 6: typeLabel };

    let index = 0;

    UsageViewModel.forEach((item) => {
        if (has(types, item.type)) {
            tObject[2 * index] = t(item.displayNameKey);
            tObject[2 * index + 1] = (index > 0 ? ` ${t(l.And)} ` : '') + getNamesString(types[item.type]);

            index++;
        }
    });

    return t(isUsedInObjectsLabel, tObject);
};

export const readFileAsync = (fileData: File): Promise<Uint8Array> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsArrayBuffer(fileData);
        reader.onload = () => {
            resolve(new Uint8Array(reader.result as ArrayBufferLike));
        };
        reader.onerror = reject;
    });
};

export const readFileBase64StringAsync = (fileData: File): Promise<Nullable<string | ArrayBuffer>> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(fileData);
        reader.onload = () => {
            resolve(reader.result);
        };
        reader.onerror = reject;
    });
};

export const readTextFileAsync = (fileData: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            resolve(reader.result as string);
        };
        reader.onerror = reject;
        reader.readAsText(fileData);
    });
};

export const getPlusOneDayDate = () => {
    return new Date().setDate(new Date().getDate() + 1);
};

export const saveToDisk = (data, type, fileName) => {
    const { fileType, fileExtension } = DownloadFileTypes[type];
    const blob = new Blob([data], { type: fileType });
    const a = document.createElement('a');
    document.body.appendChild(a);

    (a as unknown as Omit<HTMLAnchorElement, 'style'> & { style: string }).style = 'display: none';
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = fileName + fileExtension;
    a.click();
    window.URL.revokeObjectURL(url);
};

export const downloadDropdownDataCreator = (t: TFunction) => {
    return [
        { value: null, label: t(l.All) },
        { value: '10', label: t(l.TopResultsByCount, { count: 10 }) },
        { value: '20', label: t(l.TopResultsByCount, { count: 20 }) },
        { value: '50', label: t(l.TopResultsByCount, { count: 50 }) },
        { value: '100', label: t(l.TopResultsByCount, { count: 100 }) },
    ];
};

export const checkDataCount = (dataCount, count, pageNumber, pageSize) => {
    return pageNumber !== 0 && dataCount === count + pageNumber * pageSize;
};

export const getFlagsValues = (value) => {
    const result: string[] = [];
    let count = 0;
    while (!isNil(value) && value >> count !== 0) {
        if (((value >> count) & 1) !== 0) {
            result.push(Math.pow(2, count).toString());
        }
        count++;
    }
    return result;
};

export const isOnlyMinus = (value) => {
    return value === '-';
};

export const hasInFilters = (filters, action) => {
    const config = ModalActionsConfig[action];
    if (!isNil(ModalActionsConfig[action]) && config.independent !== true) {
        if (isNil(filters) || !isArray(filters)) {
            return true;
        }
        const tmpFilters = filters.reduce((acc, item) => {
            if (!isNil(item?.Name)) {
                acc[item.Name] = item;
            }
            return acc;
        }, {});

        const filter = tmpFilters[config.dataKey ?? ''];
        if (isNil(filter?.Values) || isEmpty(filter.Values)) {
            return true;
        }
        const typeKey = isArray(config.typeKey) ? config.typeKey : [config.typeKey];
        typeKey.forEach((key) => {
            if (filter.Values.includes(key.toString()) || filter.Values.includes(+key) || filter.Values.includes(key)) {
                return true;
            }
        });
    }
    return false;
};

export const getPaginationPageNumber = (listCount, selectorSize, currentPageNumber, listRemovedCount) => {
    if (currentPageNumber === 1) {
        return 1;
    }
    const newPagesCount = Math.ceil((listCount - listRemovedCount) / selectorSize);
    if (newPagesCount <= currentPageNumber) {
        return newPagesCount > 0 ? newPagesCount : 1;
    }
    return currentPageNumber;
};

export const getPageNumber = (filters, action, listCount, selectorSize, currentPageNumber, listRemovedCount) => {
    if (hasInFilters(filters, action)) {
        return currentPageNumber;
    }

    return getPaginationPageNumber(listCount, selectorSize, currentPageNumber, listRemovedCount);
};

export const isObjectsEquals = (firstObj, secondObj) => {
    return JSON.stringify(firstObj) === JSON.stringify(secondObj);
};

export const getBlockType = (typeName?: string): string => {
    return last(typeName?.split('.')) ?? '';
};

export const itemsIsValid = (items) => {
    return items.reduce((acc, item) => {
        acc = acc && item.isValid;
        return acc;
    }, true);
};

export const createTranslatableEventConfig = (value, type) => {
    return `EVENT_${value}_${type.toUpperCase()}`;
};

export const getDomain = (hostname) => {
    try {
        const matched = hostname.match(/^(?:.*?\.)?(?<domain>[a-zA-Z0-9\-_]{3,}\.(?:\w{2,8}|\w{2,4}\.\w{2,4}))$/);
        return matched.groups.domain;
    } catch {
        return '';
    }
};

export const replaceDomainFromEnvironmentURL = (url?: string): string => {
    if (!url) return '';

    // Prepend protocol if it's missing
    const fullUrl = /^https?:\/\//i.test(url) ? url : `${window.location.protocol}//${url}`;

    if (process.env.REACT_APP_MODE === 'development') {
        return fullUrl;
    }

    const parsedUrl = new URL(fullUrl);

    return parsedUrl.toString();
};

const IMAGE_PLACEHOLDER = '{ContentUrl}';

export const replaceContentUrlWithPlaceholder = (text: string, imagePlaceholder = IMAGE_PLACEHOLDER): string => {
    const contentUrl = replaceDomainFromEnvironmentURL(process.env.REACT_APP_CRM_CONTENT_URL);
    if (!contentUrl || !text) return text;

    const escapedUrl = contentUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const regex = new RegExp(escapedUrl, 'g');

    return text.replace(regex, imagePlaceholder);
};

export const injectContentUrl = (text: string, imagePlaceholder = IMAGE_PLACEHOLDER): string => {
    if (!text) return text;
    const contentUrl = replaceDomainFromEnvironmentURL(process.env.REACT_APP_CRM_CONTENT_URL);
    if (!contentUrl) return text;

    return text.replace(new RegExp(imagePlaceholder, 'g'), contentUrl);
};

export const getTranslatableErrorText = (t: TFunction, errorMessages, getLabel = (message) => message.errorMessage) => {
    let result = '';
    errorMessages.forEach((message: AnyObject, index) => {
        const messageLabel = getLabel(message);
        const errorParams = {};
        if (!isNil(message?.errorParams) && isObject(message.errorParams)) {
            keys(message.errorParams).forEach((key) => {
                if (isString(message.errorParams[key])) {
                    errorParams[key] = t(message.errorParams[key]);
                } else if (isObject(message.errorParams[key])) {
                    const errorDetail = message.errorParams[key] as AnyObject;
                    if (!isNil(errorDetail.value)) {
                        errorParams[key] =
                            !isNil(errorDetail.isTranslated) && errorDetail.isTranslated
                                ? errorDetail.value
                                : t(errorDetail.value);
                    }
                }
            });
        }

        result += t(messageLabel, { ...errorParams });
        if (index !== errorMessages.length - 1) {
            result += '\n';
        }
    });
    return result;
};

export const calculateTemplateBodyLengthWithRegEx = (regexConfig, value) => {
    regexConfig.forEach((obj) => {
        const re = new RegExp(obj?.Pattern, 'g');
        value = value.replace(re, obj?.Replacement);
    });
    return value;
};

export const hasLabel = (data, label) => (data & label) === label;

/**
 *
 * @param {number} num
 * @param {{ type: FormatNumberActionEnum.DecimalPrecision; precision?: number; isClean?: boolean; } | { type: FormatNumberActionEnum.CleanNumber; precision?: number; minPrecision?: number; maxPrecision?: number; returnType?: 'string' | 'number'; }} action
 * @returns {number}
 * @description Formats a number based on the specified action.
 */
export const formatNumber = (
    num: Nilable<number | string> = undefined,
    action:
        | {
              type: FormatNumberActionEnum.DecimalPrecision;
              precision?: number;
              isClean?: boolean;
          }
        | {
              type: FormatNumberActionEnum.CleanNumber;
              precision?: number;
              minPrecision?: number;
              maxPrecision?: number;
              returnType?: 'string' | 'number';
          } = {
        type: FormatNumberActionEnum.DecimalPrecision,
    },
) => {
    if (isNil(num)) return num;

    const cleanupString = (str: string): string => {
        return str
            .replace(/(\.\d*?[1-9])0+$/, '$1') // remove trailing zeros
            .replace(/\.0+$/, ''); // remove ".0", ".00", etc.
    };

    const decimalPrecision = (num: number | string, precision = 2, isClean = false) => {
        const tmpNum = (+num)
            .toFixed(precision)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return isClean ? cleanupString(tmpNum) : tmpNum;
    };

    const cleanNumber = (
        num: number | string,
        minPrecision = 2,
        maxPrecision = 5,
        returnType: 'string' | 'number' = 'string',
    ) => {
        if (Number.isNaN(num) || +num === 0) return 0;
        const tmpNum = +num;

        let rounded = +tmpNum.toFixed(minPrecision);

        if (rounded === 0 && tmpNum !== 0) {
            rounded = +tmpNum.toFixed(maxPrecision);
        }

        let str = rounded.toString();

        if (str.includes('.')) {
            str = cleanupString(str);
        }

        return returnType === 'number' ? +str : str;
    };

    switch (action.type) {
        case FormatNumberActionEnum.DecimalPrecision:
            return decimalPrecision(num, action.precision, action.isClean);
        case FormatNumberActionEnum.CleanNumber:
            return cleanNumber(num, action.minPrecision, action.maxPrecision, action.returnType);
        default:
            return num;
    }
};

// Decimals kept when converting an amount by a currency rate.
const RATE_CONVERSION_PRECISION = 9;

/**
 * Converts one amount into every currency in `currencyIds` using the partner rate map.
 *
 * @param {string[]} currencyIds - Currencies to fill.
 * @param {{ amount: string | number; fromCurrency: string; currencyRates: Nilable<CurrencyRates>; precision?: number; }} options
 * @returns {CurrencyAmounts} Currency id -> converted amount, '' where no usable rate exists.
 */
export const convertAmountToCurrencies = (
    currencyIds: string[],
    {
        amount,
        fromCurrency,
        currencyRates,
        precision = RATE_CONVERSION_PRECISION,
    }: {
        amount: string | number;
        fromCurrency: string;
        currencyRates: Nilable<CurrencyRates>;
        precision?: number;
    },
): CurrencyAmounts =>
    currencyIds.reduce<CurrencyAmounts>((acc, currencyId) => {
        const rate = currencyRates?.[currencyId]?.[fromCurrency]?.Rate ?? 0;
        // toFixed keeps tiny amounts out of exponential notation ('1.1e-7'), which the amount regexp rejects.
        const converted = (rate * +amount).toFixed(precision).replace(/\.?0+$/, '');

        // Currencies with no usable rate stay empty - conversion never writes a value the form would reject.
        acc[currencyId] = POSITIVE_AMOUNT_REGEXP.test(converted) ? converted : '';

        return acc;
    }, {});

/**
 * Accumulates values from multiple enabled states into a configuration object.
 * It iterates through states in order ("0", "1", "2", etc.) and fills
 * each config value with the first valid data it finds. Once a value is
 * set, it will not be overwritten by a later state.
 *
 * @param {object} config - The base configuration object with empty 'value' fields.
 * @param {object} state - The state object to search through.
 * @returns {object} A new, populated configuration object.
 */
export const accumulateConfigFromStates = (config, state) => {
    // 1. Create a deep copy of the config. This is what we will fill in.
    const updatedConfig = JSON.parse(JSON.stringify(config));

    // 2. Get the keys from the state and sort them to ensure a predictable order.
    const sortedStateKeys = Object.keys(state).sort((a, b) => Number(a) - Number(b));

    // 3. Iterate through each state entry IN ORDER.
    for (const key of sortedStateKeys) {
        const entry = state[key];

        // Process only if the entry is enabled and has dynamic variables.
        if (entry.isEnabled && entry.dynamicVariables) {
            // 4. Look at each variable this enabled state provides.
            for (const varKey in entry.dynamicVariables) {
                // 5. CRITICAL CHECK: Does our config need this variable AND is its value still empty?
                if (
                    // eslint-disable-next-line no-prototype-builtins
                    updatedConfig.hasOwnProperty(varKey) &&
                    Object.keys(updatedConfig[varKey].value).length === 0
                ) {
                    // If the slot is empty, fill it with the value from the current state.

                    if (!isEmpty(entry.dynamicVariables[varKey].value)) {
                        // update only if there IS a value
                        updatedConfig[varKey].isChecked = true;
                        updatedConfig[varKey].value = entry.dynamicVariables[varKey].value;
                    }
                }
            }
        }
    }

    return updatedConfig;
};

export const getCurrencyLabel = (originalLabel, value, currency, defaultCurrencyId, currencyRates) => {
    if (currency === defaultCurrencyId || isEmpty(currency) || !has(currencyRates, currency)) {
        return originalLabel;
    } else {
        const result = (currencyRates[currency]?.Rate * +value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return `${originalLabel} / ${result} ${defaultCurrencyId}`;
    }
};

export const isCurrentDateWithinPeriod = (startDate, daysCount) => {
    const startDateObj = moment(startDate);
    const endDateObj = moment(startDate).add(daysCount, 'days');
    const dateNow = Date.now();

    return startDateObj.toDate().getTime() < dateNow && dateNow < endDateObj.toDate().getTime();
};

export const getBonusSupportedCurrencies = (bonus) => {
    const bonusDetails = bonus?.BonusDetails ?? [];
    if (isEmpty(bonusDetails)) {
        return ((JSON.parse(bonus?.InternalDesc ?? null) ?? {}).MoneyRequirenments ?? []).map(
            (item) => item?.CurrencyId,
        );
    }
    return bonusDetails.map(({ CurrencyCode }) => CurrencyCode);
};

export const getMultiselectDropdownValueAndMap = (selections) => {
    const options = selections.reduce(
        (acc, selection) => {
            acc.values.push(selection.value);
            acc.valueMap[selection.value] = true;

            return acc;
        },
        { values: [], valueMap: {} },
    );

    return options;
};

/**
 * Maps a flat list of partners into a grouped model based on PartnerGroup.
 * Each partner within a group will have a displayId derived from PartnerExternalConnections
 * or fall back to PartnerId.
 *
 * @param {Array<Object>} partnersList - The original flat list of partner objects.
 * @param {string} partnerDataSourceId - The ID of the partner data source to find the external ID.
 * @returns {Array<Object>} An array of PartnerGroupModel objects.
 */
export const mapToPartnerGroupsModel = (partnersList, partnerDataSourceId): GroupedOption[] => {
    const resultObject = partnersList.reduce((acc, val) => {
        const groupData = val.PartnerGroup;

        // Initialize the group if it doesn't exist in the accumulator
        if (!acc[groupData.PartnerGroupId]) {
            acc[groupData.PartnerGroupId] = {
                groupId: groupData.PartnerGroupId,
                groupName: groupData.Name,
                groupDescription: groupData.Description,
                items: [], // Initialize items as an empty array
            };
        }

        // Find the external ID based on the provided partnerDataSourceId
        const partnerExternalId = val.PartnerExternalConnections.find(
            (externalConnection) => externalConnection.PartnerDataSource.PartnerDataSourceId === partnerDataSourceId,
        )?.ExternalId; // Using optional chaining for safety

        // Push the partner data into the correct group's items array
        acc[groupData.PartnerGroupId].items.push({
            ...val, // Spread all existing properties of the partner
            displayId: partnerExternalId ?? val.PartnerId, // Use externalId if found, otherwise PartnerId
        });

        return acc; // Return the accumulator for the next iteration
    }, {}); // Initial value of the accumulator is an empty object

    // Convert the object of grouped partners into an array of its values
    return Object.values(resultObject);
};

export const componentToHex = (c) => {
    const hex = c.toString(16);
    // eslint-disable-next-line eqeqeq
    return hex.length == 1 ? '0' + hex : hex;
};

export const rgbToHex = (r, g, b) => '#' + componentToHex(r) + componentToHex(g) + componentToHex(b);

export const getHexColorWithOpacity = (color: string | [number, number, number], opacity: number) => {
    return (
        (typeof color === 'string' && color.startsWith('#')
            ? color
            : rgbToHex(...(color as [number, number, number]))) + HexOpacityCodes[opacity]
    );
};

export const getTemplateChannelIconWithLabel = (type, iconAdditionalClassName = '') => {
    const getIconType = (type) => {
        switch (type) {
            case TemplateTypesLabels.Email:
                return 'bc-icon-send-email-block crm-template-type-icon email';
            case TemplateTypesLabels.Internal:
                return 'bc-icon-sms crm-template-type-icon internal';
            case TemplateTypesLabels.SMS:
                return 'bc-icon-message-closed crm-template-type-icon sms';
            case TemplateTypesLabels.Hoory:
                return 'bc-icon-hoory';

            case TemplateTypesLabels.PopUp:
                return 'bc-icon-send-pop-up-block crm-template-type-icon popup';
            case TemplateTypesLabels.DynamicPopUp:
                return 'bc-icon-send-pop-up-block crm-template-type-icon popup';
            case TemplateTypesLabels.CMSPopUp:
                return 'bc-icon-send-pop-up-block crm-template-type-icon popup';
            case TemplateTypesLabels.CMSBanner:
                return 'bc-icon-desktop-preview crm-template-type-icon banner';
            case TemplateTypesLabels.Telegram:
                return 'bc-icon-send-telegram-message-block crm-template-type-icon telegram';
            case TemplateTypesLabels.PushNotification:
                return 'bc-icon-bell-outline crm-template-type-icon push-notification';
            case TemplateTypesLabels.Viber:
                return 'bc-icon-viber';
            case TemplateTypesLabels.WhatsApp:
                return 'bc-icon-whatsapp crm-template-type-icon whatsapp';
            case TemplateTypesLabels.WhatsAppProvider:
                return 'bc-icon-whatsapp crm-template-type-icon whatsapp';
            default:
                return null;
        }
    };

    return (
        <>
            <Icon type={`${getIconType(type)} ${iconAdditionalClassName}`} />
            <span>{TemplateTypesLabels[type]}</span>
        </>
    );
};

export const replaceDayTokens = (str: Nilable<string>) => {
    if (isNil(str)) return null;
    return str
        .replace(/(^|[^d])d{1,2}(?!d)/g, (match) => {
            return match.replace(/d/g, 'D');
        })
        .replace(/y{2,4}/g, (match) => {
            return match.replace(/y/g, 'Y');
        });
};

export const parseToSegmentKpiConfig = (supportedCases: KPISupportedCase[] = []) =>
    supportedCases.reduce((acc, item) => {
        if (!has(acc, DynamicBonusTypes[item.Type])) {
            acc[DynamicBonusTypes[item.Type]] = [];
        }
        acc[DynamicBonusTypes[item.Type]].push(item.Id);
        return acc;
    }, {} as BonusSupportedCases);

export const getBitwiseValue = (arrayOfValues) => {
    return arrayOfValues.reduce((acc, val) => {
        if (acc === null) {
            acc = val;
        }
        return (acc |= val);
    }, null);
};

export const formatRecurrenceDates = (upcomingExecutions, format) => {
    if (isEmpty(upcomingExecutions)) {
        return [];
    }
    return upcomingExecutions.map((executionDate) => {
        return RecurrenceDataColumnsConfig.reduce((acc, { columnName }) => {
            acc[columnName] = customMomentWithoutTimezoneConversion(executionDate[columnName]).format(format);
            return acc;
        }, {});
    });
};

export const clearEmptyValuesFromObject = (obj) => omitBy(obj, isNil);

export const generateCleanUrlParams = (queryParams = {}, shouldEncode = false) => {
    const cleanedParams = omitBy(queryParams, (value) => isNil(value) || value === 'null' || value === 'undefined');

    return combineUrlParams(cleanedParams, shouldEncode);
};

export const recursiveSumObject = (obj) => {
    return values(obj).reduce((acc, value) => {
        return acc + (isObject(value) ? recursiveSumObject(value) : value);
    }, 0);
};

export const genericMemo: <T>(component: T) => T = memo;

export const getPreCalculationResultText = (t: TFunction, preCalculation: Nilable<number>) => {
    if (isNil(preCalculation)) {
        return t(l.Off);
    }
    if (preCalculation === 0) {
        return t(l.Immediately);
    }
    return t(l.HoursWithCount, { count: preCalculation });
};

export const scrollRefIntoView = (ref) => {
    if (!isNil(ref)) {
        ref.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });
    }
};

/**
 * Rejection handler for direct request calls (not via doRequest). A cancelled call (unmount or a newer call
 * superseded it) is expected and ignored; any other error is logged.
 *
 * @example
 * getPartnerLanguageList(dispatch, getPartnerLanguage.request).catch(ignoreCanceledError);
 */
export const ignoreCanceledError = (error: unknown): void => {
    if (!(error instanceof CanceledError)) {
        console.error(error);
    }
};

export const getPartnerLanguageList = (
    dispatch: Dispatch<Action<string> | UnknownAction>,
    getPartnerLanguages: CancelableRequest<PartnerLanguage[]>['request'],
) => {
    return getPartnerLanguages().then(({ Data }) => {
        subscribe(GlobalEvents.PartnerChanged, SubscriberSources.PartnerLanguage, () =>
            dispatch(resetPartnerLanguages()),
        );

        dispatch(setPartnerLanguages(Data));
        return Data;
    });
};

export const getExclusionRulesQueryConfigurations = (
    dispatch: ReturnType<typeof useAppDispatch>,
    getQueryConfigurationsList,
) => {
    return getQueryConfigurationsList().then(({ Data }) => {
        const hashedData = startHashing(Data);
        subscribe(GlobalEvents.PartnerChanged, SubscriberSources.ExclusionRulesQueryConfiguration, () =>
            dispatch(clearExclusionRulesQueryConfigurations()),
        );

        dispatch(setExclusionRulesQueryConfigurations(hashedData));
        return hashedData;
    });
};

export const getQueryConfigurations = (dispatch: ReturnType<typeof useAppDispatch>, getQueryConfigurationsList) => {
    return getQueryConfigurationsList().then(({ Data }) => {
        const hashedData = startHashing(Data);

        subscribe(GlobalEvents.PartnerChanged, SubscriberSources.QueryConfiguration, () =>
            dispatch(clearBaseQueryConfigurations()),
        );

        dispatch(setBaseQueryConfigurations(hashedData));

        return hashedData;
    });
};

const getCommunicationColor = (channel, isSuccess) => {
    const colors = CommunicationTypeColorsByStatus[channel];

    if (!colors) return '#ccc'; // fallback safety

    return isSuccess ? colors.success : colors.failed;
};

export const getCJBlockTitleLabel = (titleKey) => l[`CJBlock${titleKey}`];

export const recursiveParsePlainObjectOrArrayToString = (config?: Nilable<AnyObject | AnyObject[]>) => {
    if (isNil(config) || (!isArray(config) && !isPlainObject(config))) {
        return '';
    }

    const removeEndTrailingComma = (str: string) => {
        return str.endsWith(', ') ? str.slice(0, -2) : str;
    };

    let tmpString = '';

    if (isArray(config)) {
        tmpString = config
            .map((item) => {
                if (isUndefined(item)) {
                    return null;
                }

                if (isNull(item)) {
                    return 'null';
                }

                if (isNumber(item) || isString(item) || isBoolean(item)) {
                    return `${item}`;
                }

                const parsedItem = recursiveParsePlainObjectOrArrayToString(item);
                return parsedItem === '' ? null : parsedItem;
            })
            .filter((item) => !isNil(item))
            .join(', ');
    } else {
        tmpString = Object.entries(config).reduce((acc, [key, value]) => {
            if (isUndefined(value)) {
                return acc;
            }

            if (isNull(value)) {
                return acc + `${key}: null, `;
            }

            if (isObject(value)) {
                if (isEmpty(value)) return acc;

                if (isArray(value)) {
                    const val = removeEndTrailingComma(recursiveParsePlainObjectOrArrayToString(value));
                    return val ? acc + `${key}: [ ${val} ], ` : acc;
                }

                const val = removeEndTrailingComma(recursiveParsePlainObjectOrArrayToString(value));
                return val ? acc + `${key}: { ${val} }, ` : acc;
            }

            return acc + `${key}: ${value}, `;
        }, '');
    }

    return removeEndTrailingComma(tmpString);
};

export const parseNotificationContentMessage = (notificationItem, t) => {
    const { ListItem, ContentMessage } = notificationItem;

    let newContent = l.Object;
    if (!ListItem) {
        return newContent;
    }
    switch (ListItem.ObjectTypeId) {
        case ObjectTypeIdsEnum.Campaign:
            newContent = l.Campaign;
            break;
        case ObjectTypeIdsEnum.RealtimePromotion:
            newContent = RealtimePromotionTypes[ListItem.PromotionType];
            break;
        case ObjectTypeIdsEnum.Report:
            newContent = ReportTypesLabels[ListItem.ReportType];
            break;
        default:
            newContent = l.Object;
    }

    const result = ContentMessage.replace('{type}', t(newContent));
    return result;
};

export const generateInternalImageUrl = (src: string, domain = process.env.REACT_APP_CRM_CONTENT_URL) => {
    if (!src || typeof src !== 'string') return '';

    if (/^(https?:)?\/\//i.test(src) || /^(data|blob):/i.test(src)) {
        return src;
    }

    const host = replaceDomainFromEnvironmentURL(domain).replace(/\/$/, '');

    const normalizedPath = src.startsWith('/') ? src : `/${src}`;

    return `${host}${normalizedPath}`;
};

/**
 * Returns the URL only when it resolves to an `http:` or `https:` page, otherwise `undefined`.
 * Use it for every `href`, `src` and `window.open` that receives a stored or user-entered value:
 * React 17 renders `javascript:` links as they are, so the scheme check has to happen here.
 */
export const getSafeHttpUrl = (url: Nilable<string>): string | undefined => {
    if (!isString(url) || isEmpty(url.trim())) return undefined;

    try {
        const { protocol } = new URL(url.trim(), window.location.href);
        return protocol === 'http:' || protocol === 'https:' ? url : undefined;
    } catch {
        return undefined;
    }
};

export const doesBonusHaveExpiration = (bonusType: Nilable<AwardTypesEnum>) => {
    if (isNil(bonusType)) return false;
    return [AwardTypesEnum.WonderWheelSpin, AwardTypesEnum.NftVoucherBonus].includes(bonusType);
};

export const generateTagComponentColor = (color: string) => {
    let tmpColor: string | undefined = color;
    if (['confirm', 'warning', 'danger', 'note', 'message', 'background', 'foreground'].includes(color)) {
        tmpColor = `var(--${color})`;
    } else if (color === 'hero') {
        tmpColor = undefined;
    }
    return tmpColor;
};

export const buildCustomFieldsRequestModel = (customField: AnyObject) => {
    if (isEmpty(customField)) {
        return [];
    }

    return Object.entries(customField).map(([key, value]) => ({
        CustomFieldId: +key.split('_')[1],
        FieldValue: String(value),
    }));
};

export default {
    generateRequestFilterField,
    queryParamsDecoder,
    nativeQueryParamsDecoder,
    queryParamsEncoder,
    combineUrlParams,
    openUrlInNewTab,
    getByteLen,
    cronStringEncoder,
    cronStringDecoder,
    getDate,
    getDateFormat,
    monthArrayForDropdown,
    getDays,
    hexToRgb,
    getUsedInConfirmationText,
    getDateWithTime,
    readFileAsync,
    getPlusOneDayDate,
    customMoment,
    customMomentWithTimezoneConversion,
    customMomentWithoutTimezoneConversion,
    saveToDisk,
    downloadDropdownDataCreator,
    checkDataCount,
    weeksArrayForDropdown,
    getStringElementsByIndexArray,
    getDataTableKey,
    getFilterHolderKey,
    getFiltersTagKey,
    getFlagsValues,
    readFileBase64StringAsync,
    readTextFileAsync,
    mapToPartnerGroupsModel,
    isOnlyMinus,
    getPageNumber,
    isObjectsEquals,
    getBlockType,
    itemsIsValid,
    createTranslatableEventConfig,
    getMultiselectDropdownValueAndMap,
    replaceDomainFromEnvironmentURL,
    accumulateConfigFromStates,
    getTranslatableErrorText,
    calculateTemplateBodyLengthWithRegEx,
    formatNumber,
    convertAmountToCurrencies,
    hasLabel,
    getCurrencyLabel,
    getBonusSupportedCurrencies,
    convertMinutesToHHMM,
    getHexColorWithOpacity,
    getTemplateChannelIconWithLabel,
    parseToSegmentKpiConfig,
    isCurrentDateWithinPeriod,
    getBitwiseValue,
    replaceDayTokens,
    formatRecurrenceDates,
    clearEmptyValuesFromObject,
    generateCleanUrlParams,
    recursiveSumObject,
    getCommunicationColor,
    generateInternalImageUrl,
    getSafeHttpUrl,
    generateTagComponentColor,
    buildCustomFieldsRequestModel,
};
